import { API_TIMEOUT_MS } from './constants.ts';
import { env } from './env.ts';
import { getSessionToken } from './session.ts';

/** HTTP 상태와 파싱된 본문을 함께 들고 다니는 에러 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  readonly url: string;

  constructor(status: number, body: unknown, url: string) {
    super(`API Error ${status}: ${url}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.url = url;
  }
}

/** 타임아웃으로 끊겼을 때 */
export class ApiTimeoutError extends Error {
  readonly url: string;

  constructor(url: string) {
    super(`API Timeout: ${url}`);
    this.name = 'ApiTimeoutError';
    this.url = url;
  }
}

export type Query = Record<string, string | number | boolean | null | undefined>;

export type ApiInit = Omit<RequestInit, 'method' | 'body'> & {
  query?: Query;
  timeoutMs?: number;
  body?: unknown;
};

/** 401 응답을 앱 전역에서 한 번만 처리하기 위한 훅 (예: 로그아웃 후 /login 이동) */
let unauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

function toQueryString(query?: Query): string {
  if (!query) return '';

  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === null || v === undefined) continue;
    sp.set(k, String(v));
  }

  const s = sp.toString();
  return s ? `?${s}` : '';
}

/**
 * 요청에 실을 로그인 토큰.
 *
 * 예전에는 `token` 쿠키를 읽었는데, 그 쿠키는 구 QR 플레이어의 **접속 토큰**
 * (MC_CONNECT)이고 로그인과 무관했다. 우리 SPA 에서는 거의 항상 비어 있었다.
 */
function getAccessToken(): string | null {
  return getSessionToken();
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204) return null;

  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) return res.json().catch(() => null);
  return res.text().catch(() => '');
}

/** 호출자 signal 과 타임아웃 signal 을 합쳐 둘 중 먼저 발생한 쪽으로 취소한다. */
function withTimeout(signal: AbortSignal | null | undefined, timeoutMs: number) {
  const timeoutSignal = AbortSignal.timeout(timeoutMs);
  return signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;
}

async function request<T>(method: string, path: string, init: ApiInit = {}): Promise<T> {
  const { query, timeoutMs = API_TIMEOUT_MS, body: rawBody, signal, ...options } = init;
  const url = `${env.apiBase}${path}${toQueryString(query)}`;

  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');

  const token = getAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  // plain object 는 JSON 으로 자동 변환. FormData/Blob 등은 그대로 넘긴다.
  let body: BodyInit | undefined;
  if (
    rawBody instanceof FormData ||
    rawBody instanceof Blob ||
    rawBody instanceof URLSearchParams
  ) {
    body = rawBody;
  } else if (typeof rawBody === 'string') {
    body = rawBody;
  } else if (rawBody !== undefined && rawBody !== null) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(rawBody);
  }

  let res: Response;
  try {
    res = await fetch(url, {
      ...options,
      method,
      headers,
      body,
      signal: withTimeout(signal, timeoutMs),
      credentials: options.credentials ?? 'include',
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'TimeoutError') {
      throw new ApiTimeoutError(url);
    }
    throw err;
  }

  const parsed = await parseBody(res);

  if (!res.ok) {
    if (res.status === 401) unauthorizedHandler?.();
    throw new ApiError(res.status, parsed, url);
  }

  return parsed as T;
}

export const api = {
  get: <T>(path: string, query?: Query, init?: ApiInit) =>
    request<T>('GET', path, { ...init, query }),

  post: <T>(path: string, body?: unknown, init?: ApiInit) =>
    request<T>('POST', path, { ...init, body }),

  put: <T>(path: string, body?: unknown, init?: ApiInit) =>
    request<T>('PUT', path, { ...init, body }),

  patch: <T>(path: string, body?: unknown, init?: ApiInit) =>
    request<T>('PATCH', path, { ...init, body }),

  delete: <T>(path: string, init?: ApiInit) => request<T>('DELETE', path, init),
};
